import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n/I18nContext";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight mb-4 text-ink-text">
      {title}
    </h2>
    <div className="space-y-4 text-sm sm:text-base text-muted leading-relaxed">
      {children}
    </div>
  </section>
);

export default function Legal({ forceLang }) {
  const { lang, setLang, isEn } = useI18n();

  useEffect(() => {
    if (forceLang && lang !== forceLang) {
      setLang(forceLang);
    }
  }, [forceLang, lang, setLang]);

  const activeEn = isEn || forceLang === "en";

  return (
    <main className="min-h-screen bg-page text-ink-text">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16 sm:py-20">
        <div className="mb-10">
          <Link
            to={activeEn ? "/en" : "/"}
            className="mono text-[11px] uppercase tracking-[0.22em] text-brand-cyan hover:text-brand-cyan/80 transition-colors"
          >
            ← {activeEn ? "Back to Home" : "На главную"}
          </Link>
        </div>

        <div className="glass rounded-3xl p-8 sm:p-10 lg:p-12">
          {activeEn ? (
            <div>
              <div className="mb-10">
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-brand-cyan mb-4">
                  Legal Notice
                </div>
                <h1 className="text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-tight mb-4">
                  Legal Information
                </h1>
                <p className="text-muted leading-relaxed max-w-3xl">
                  The materials on this website are provided for informational purposes regarding the InBoost AI Engine platform, architecture, benchmarks, and deployment models.
                </p>
              </div>

              <Section title="1. Informational Purpose">
                <p>
                  All content published on inboost.pro and associated subdomains is provided exclusively for general informational evaluation of InBoost technologies, algorithms, and architectural capabilities.
                </p>
                <p>
                  The information presented does not constitute binding technical specifications, service level agreements, or contractual representations unless formalized in a mutually executed Master Services Agreement.
                </p>
              </Section>

              <Section title="2. Intellectual Property & Patents">
                <p>
                  The InBoost AI Engine software architecture, proprietary scheduling algorithms, Scoring Function formulations (CEI, ZCR), and RL-driven telemetry models are protected by international patents, patent applications, and intellectual property treaties.
                </p>
                <p>
                  All third-party trademarks, product names, and company logos (including Kubernetes, NVIDIA, RTX, NVLink, DCGM, MIG, Run:ai, and others) cited on this website are the property of their respective trademark holders. Reference to them does not imply affiliation, sponsorship, or endorsement.
                </p>
              </Section>

              <Section title="3. Limitation of Liability">
                <p>
                  InBoost makes reasonable efforts to ensure the accuracy of benchmark figures and simulation metrics. However, production performance and ROI depend on specific datacenter hardware, interconnect topology, and workload dynamics.
                </p>
              </Section>

              <Section title="4. Contact">
                <p>
                  For enterprise inquiries, pilot requests, or legal notices, please reach out via{" "}
                  <a href="mailto:inbound@inboost.pro" className="text-brand-cyan hover:underline">
                    inbound@inboost.pro
                  </a>.
                </p>
              </Section>
            </div>
          ) : (
            <div>
              <div className="mb-10">
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-brand-cyan mb-4">
                  Legal
                </div>
                <h1 className="text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-tight mb-4">
                  Правовая информация
                </h1>
                <p className="text-muted leading-relaxed max-w-3xl">
                  Материалы сайта предназначены для общего ознакомления с платформой InBoost,
                  её технологическими возможностями, сценариями внедрения и архитектурными подходами.
                </p>
              </div>

              <Section title="1. Информационный характер материалов">
                <p>
                  Материалы, размещенные на сайте inboost.pro, носят исключительно информационный характер
                  и предназначены для общего ознакомления с платформой InBoost, архитектурными подходами,
                  технологическими возможностями, вариантами внедрения и сценариями использования.
                </p>
                <p>
                  Информация, опубликованная на сайте, не является исчерпывающей и не заменяет техническую,
                  проектную, договорную, юридическую или иную профессиональную документацию.
                </p>
              </Section>

              <Section title="2. Отсутствие публичной оферты">
                <p>
                  Ничто на сайте не является публичной офертой в смысле статьи 437 Гражданского кодекса
                  Российской Федерации, если иное прямо не указано в отдельном документе,
                  подписанном или опубликованном Оператором.
                </p>
              </Section>

              <Section title="3. Интеллектуальная собственность">
                <p>
                  Все права на результаты интеллектуальной деятельности, размещенные на сайте, включая
                  дизайн, текстовые материалы, графические изображения, элементы интерфейса, архитектурные
                  схемы и описания технологий, принадлежат ООО «ИнБуст» или используются на законных
                  основаниях.
                </p>
              </Section>

              <Section title="4. Контакты">
                <p>
                  По вопросам правового характера, партнерства или взаимодействия вы можете направить
                  запрос по официальному адресу электронной почты:{" "}
                  <a href="mailto:inbound@inboost.pro" className="text-brand-cyan hover:underline">
                    inbound@inboost.pro
                  </a>.
                </p>
              </Section>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}